"use client";

import * as React from "react";
import type { PageHeaderV2Config } from "components/ui/v2/page-header-v2";
import { PageEmptyStateV2 } from "components/ui/v2/maintainer/page-empty-state-v2";
import { PageMetaBarV2 } from "components/ui/v2/maintainer/page-meta-bar-v2";
import { PagePaginationFooterV2 } from "components/ui/v2/maintainer/page-pagination-footer-v2";
import { PageToolbarV2 } from "components/ui/v2/maintainer/page-toolbar-v2";
import { PageShell, type PageShellProps } from "./page-shell";

export interface ListPageShellProps
  extends Omit<
    PageShellProps,
    "toolbar" | "metaBar" | "paginationFooter" | "loading" | "emptyState"
  > {
  header: React.ReactNode | PageHeaderV2Config;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  clearSearchLabel?: string;
  filters?: React.ReactNode;
  toolbar?: React.ReactNode;
  loading?: boolean;
  loadingTitle?: string;
  loadingDescription?: string;
  isEmpty?: boolean;
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyState?: React.ReactNode;
  emptyActions?: React.ReactNode;
  metaSummary?: React.ReactNode;
  metaPageInfo?: React.ReactNode;
  metaIcon?: React.ComponentType<{ className?: string }>;
  showMetaBar?: boolean;
  children?: React.ReactNode;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  rangeLabel?: React.ReactNode;
  perPageLabel?: string;
}

export function ListPageShell({
  header,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  clearSearchLabel,
  filters,
  toolbar,
  beforeContent,
  featuredList,
  loading = false,
  loadingTitle = "Cargando...",
  loadingDescription,
  isEmpty = false,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyState,
  emptyActions,
  metaSummary,
  metaPageInfo,
  metaIcon,
  showMetaBar = true,
  children,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  rangeLabel,
  perPageLabel,
  helpTooltips,
  overlays,
  fab,
}: ListPageShellProps) {
  const hasSearch = onSearchChange !== undefined;
  const hasToolbar = Boolean(toolbar || hasSearch || filters);
  const hasPagination =
    totalPages !== undefined &&
    totalPages > 1 &&
    onPageChange !== undefined &&
    currentPage !== undefined;

  const toolbarNode =
    toolbar ??
    (hasToolbar ? (
      <PageToolbarV2
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        clearSearchLabel={clearSearchLabel}
        filters={filters}
      />
    ) : null);

  // Initial load only: loading + empty. Keeps table visible on refetch.
  const showInitialLoading = Boolean(loading && isEmpty);
  const hasData = !isEmpty && Boolean(children);
  const showPaginationFooter = hasData && hasPagination;
  const searchActive = Boolean(searchValue?.length);

  const resolvedEmptyState =
    emptyState ??
    (emptyIcon && emptyTitle ? (
      <PageEmptyStateV2
        variant={searchActive ? "noResult" : "empty"}
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        onClearSearch={searchActive ? () => onSearchChange?.("") : undefined}
        clearSearchLabel={clearSearchLabel}
        actions={emptyActions}
      />
    ) : null);

  return (
    <PageShell
      header={header}
      beforeContent={beforeContent}
      featuredList={featuredList}
      toolbar={toolbarNode}
      loading={showInitialLoading}
      loadingTitle={loadingTitle}
      loadingDescription={loadingDescription}
      isEmpty={isEmpty}
      emptyState={resolvedEmptyState}
      metaBar={
        hasData && showMetaBar && metaSummary && metaIcon ? (
          <PageMetaBarV2
            icon={metaIcon}
            summary={metaSummary}
            pageInfo={metaPageInfo}
          />
        ) : null
      }
      paginationFooter={
        showPaginationFooter ? (
          <PagePaginationFooterV2
            currentPage={currentPage!}
            totalPages={totalPages!}
            totalItems={totalItems ?? 0}
            pageSize={pageSize ?? 0}
            onPageChange={onPageChange!}
            rangeLabel={rangeLabel}
            perPageLabel={perPageLabel}
          />
        ) : null
      }
      helpTooltips={helpTooltips}
      overlays={overlays}
      fab={fab}
    >
      {!showInitialLoading && !isEmpty ? children : null}
    </PageShell>
  );
}
